import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { X } from 'lucide-react';

const StatusPill = ({ status }) => {
  const styles = {
    complete: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    generating: 'bg-amber-50 text-amber-700 border-amber-200',
    in_progress: 'bg-neutral-100 text-neutral-700 border-neutral-300',
    awaiting_photos: 'bg-neutral-100 text-neutral-700 border-neutral-300',
    failed: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] border ${styles[status] || 'border-neutral-300'}`}>
      {status || '—'}
    </span>
  );
};

const AdminAssessments = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get('/admin/assessments')
      .then(({ data }) => setItems(data))
      .catch(() => toast.error('Could not load assessments'))
      .finally(() => setLoading(false));
  }, []);

  const openDetail = async (id) => {
    setSelected(id);
    setDetail(null);
    try {
      const { data } = await api.get(`/admin/assessments/${id}`);
      setDetail(data);
    } catch { toast.error('Could not load full report'); }
  };

  return (
    <div>
      <h1 className="font-serif text-4xl text-sans_ink tracking-tight" data-testid="admin-assessments-title">Prakriti Assessments</h1>
      <p className="text-sans_mute mt-2">Everyone who has taken the Prakriti test — with their concern, verdict, and full report.</p>

      <div className="mt-8 bg-white border border-sans_line overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-sans_line">
              {['Date', 'Patient', 'Email', 'Concern', 'Verdict', 'V / P / K', 'Status', ''].map((h) => (
                <th key={h} className="text-left px-4 py-3 font-medium text-[11px] tracking-[0.18em] uppercase text-sans_mute">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-sans_mute">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-sans_mute">No assessments yet.</td></tr>}
            {items.map((a) => {
              const p = a.percentages || {};
              return (
                <tr key={a.assessment_id} className="border-b border-sans_line hover:bg-sans_bone/50 align-top" data-testid={`assessment-row-${a.assessment_id}`}>
                  <td className="px-4 py-3 text-sans_mute text-xs whitespace-nowrap">{new Date(a.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                  <td className="px-4 py-3 text-sans_ink">{a.user_name || '—'}</td>
                  <td className="px-4 py-3 text-sans_mute text-xs">{a.user_email || '—'}</td>
                  <td className="px-4 py-3 text-sans_ink max-w-[280px] truncate" title={a.concern || ''}>{a.concern || <span className="text-sans_mute">—</span>}</td>
                  <td className="px-4 py-3 text-sans_ink font-serif">
                    {a.primary_dosha ? `${a.primary_dosha}${a.secondary_dosha ? ` · ${a.secondary_dosha}` : ''}` : <span className="text-sans_mute font-sans text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-sans_mute text-xs tabular-nums whitespace-nowrap">
                    {p.vata != null ? `${p.vata} / ${p.pitta} / ${p.kapha}` : '—'}
                  </td>
                  <td className="px-4 py-3"><StatusPill status={a.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openDetail(a.assessment_id)} data-testid={`open-assessment-${a.assessment_id}`} className="text-xs text-sans_ink underline underline-offset-2">
                      View report
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-end" onClick={() => { setSelected(null); setDetail(null); }}>
          <div className="w-full max-w-3xl h-full bg-white overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center justify-between px-8 py-5 border-b border-sans_line bg-white">
              <div className="font-serif text-2xl text-sans_ink">Assessment detail</div>
              <button onClick={() => { setSelected(null); setDetail(null); }} className="text-sans_mute hover:text-sans_ink"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-8">
              {!detail && <div className="text-sans_mute">Loading full report…</div>}
              {detail && (
                <>
                  <div className="grid sm:grid-cols-2 gap-4 mb-8">
                    <div><div className="sans-label mb-1">Patient</div><div className="text-sans_ink">{detail.user?.name || '—'}</div><div className="text-xs text-sans_mute">{detail.user?.email}</div></div>
                    <div><div className="sans-label mb-1">Taken on</div><div className="text-sans_ink">{new Date(detail.created_at).toLocaleString('en-IN')}</div></div>
                    <div className="sm:col-span-2"><div className="sans-label mb-1">What brought them here</div><div className="text-sans_ink leading-relaxed">{detail.concern || <span className="text-sans_mute">Not provided</span>}</div></div>
                  </div>

                  {detail.report && (
                    <div className="space-y-6 border-t border-sans_line pt-6">
                      <div>
                        <div className="sans-label mb-2">Verdict</div>
                        <div className="font-serif text-3xl text-sans_ink">{detail.report.primary_dosha} · {detail.report.secondary_dosha}</div>
                        <div className="text-xs text-sans_mute mt-2 tabular-nums">Vata {detail.report.percentages?.vata}% · Pitta {detail.report.percentages?.pitta}% · Kapha {detail.report.percentages?.kapha}% · Confidence {detail.report.confidence}%</div>
                      </div>
                      <div>
                        <div className="sans-label mb-2">Summary</div>
                        <div className="text-sans_ink leading-relaxed whitespace-pre-wrap">{detail.report.summary}</div>
                      </div>
                      {detail.report.hidden_symptoms_probable?.length > 0 && (
                        <div>
                          <div className="sans-label mb-2">Likely hidden symptoms</div>
                          <ul className="list-disc pl-5 space-y-1 text-sans_ink">{detail.report.hidden_symptoms_probable.map((s,i) => <li key={i}>{s}</li>)}</ul>
                        </div>
                      )}
                      {detail.report.long_term_risks?.length > 0 && (
                        <div>
                          <div className="sans-label mb-2">Long-term risks if ignored</div>
                          <ul className="list-disc pl-5 space-y-1 text-sans_ink">{detail.report.long_term_risks.map((s,i) => <li key={i}>{s}</li>)}</ul>
                        </div>
                      )}
                      <a href={`/assessment/${detail.assessment_id}`} target="_blank" rel="noreferrer" className="inline-block mt-4 text-xs text-sans_ink underline underline-offset-2">Open the patient-facing report ↗</a>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAssessments;
